import React from 'react';
import { Image } from 'expo-image';

import { Box, Text, useTheme } from 'app/shopify/theme';

export type SignalsType = 'primary' | 'secondary';
export type SignalsSize = 'default' | 'small';
export type SignalsValidation = 'none' | 'success' | 'warning' | 'error' | 'info';

export interface SignalsProps {
  label: string;
  type?: SignalsType;
  size?: SignalsSize;
  validation?: SignalsValidation;
  inverted?: boolean;
  leadingIcon?: boolean;
}

const ICON_BY_VALIDATION_AND_SIZE: Record<SignalsSize, Record<SignalsValidation, string>> = {
  default: {
    none: 'https://www.figma.com/api/mcp/asset/339c9a88-3846-402b-9181-2e55adb36921',
    error: 'https://www.figma.com/api/mcp/asset/34a8d7fa-1a5b-4206-8aa3-25e9ea09da51',
    success: 'https://www.figma.com/api/mcp/asset/16661468-64c1-4c96-bece-50e8eb02eed1',
    warning: 'https://www.figma.com/api/mcp/asset/3a83c1f5-943d-42b6-ba06-f3f03524dd5c',
    info: 'https://www.figma.com/api/mcp/asset/7f4f6308-d8f8-4015-ab44-76ca3409f821',
  },
  small: {
    none: 'https://www.figma.com/api/mcp/asset/ac0965d9-bd63-4242-b264-5253a34d9d89',
    error: 'https://www.figma.com/api/mcp/asset/911f964f-6c76-427a-8115-53d113edd53b',
    success: 'https://www.figma.com/api/mcp/asset/2ac54935-268e-4158-b6e7-b935615ce3d9',
    warning: 'https://www.figma.com/api/mcp/asset/6f6de8f8-c121-432f-ad64-4c40d46c923f',
    info: 'https://www.figma.com/api/mcp/asset/ac19e2ff-b17f-44a1-b3e8-d834aae6edb1',
  },
};

const TEXT_COLOR_BY_VALIDATION: Record<SignalsValidation, keyof ReturnType<typeof useTheme>['colors']> = {
  none: 'text.brand',
  error: 'text.error',
  success: 'text.success',
  warning: 'text.warning',
  info: 'text.info',
};

const BACKGROUND_BY_VALIDATION: Record<SignalsValidation, keyof ReturnType<typeof useTheme>['colors']> = {
  none: 'surface.secondary',
  error: 'surface.error.subtle',
  success: 'surface.success.subtle',
  warning: 'surface.warning.subtle',
  info: 'surface.info.subtle',
};

const BORDER_BY_VALIDATION: Record<SignalsValidation, keyof ReturnType<typeof useTheme>['colors']> = {
  none: 'border.secondary',
  error: 'border.error',
  success: 'border.success',
  warning: 'border.warning',
  info: 'border.info',
};

const TEXT_VARIANT_BY_SIZE: Record<SignalsSize, 'bodyXSmall' | 'bodyCaption'> = {
  default: 'bodyXSmall',
  small: 'bodyCaption',
};

const ICON_SIZE_BY_SIZE: Record<SignalsSize, number> = {
  default: 16,
  small: 12,
};

const ROOT_PADDING_VERTICAL_BY_SIZE: Record<SignalsSize, 'spacing.general.x1' | 'spacing.general.x0,5'> = {
  default: 'spacing.general.x1',
  small: 'spacing.general.x0,5',
};

const LABEL_PADDING_HORIZONTAL_BY_SIZE: Record<SignalsSize, 'spacing.general.x1' | 'spacing.general.x0,5'> = {
  default: 'spacing.general.x1',
  small: 'spacing.general.x0,5',
};

export function Signals({
  label,
  type = 'primary',
  size = 'default',
  validation = 'none',
  inverted = false,
  leadingIcon = true,
}: SignalsProps) {
  const theme = useTheme();

  const textColor = theme.colors[TEXT_COLOR_BY_VALIDATION[validation]];
  const borderColor = theme.colors[BORDER_BY_VALIDATION[validation]];

  const backgroundColor =
    type === 'primary'
      ? theme.colors[inverted ? 'surface.default' : BACKGROUND_BY_VALIDATION[validation]]
      : 'transparent';

  const iconSize = ICON_SIZE_BY_SIZE[size];

  return (
    <Box
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      borderRadius="radius.full"
      paddingHorizontal="spacing.general.x1"
      paddingVertical={ROOT_PADDING_VERTICAL_BY_SIZE[size]}
      borderWidth={type === 'secondary' ? 1 : 0}
      style={{
        backgroundColor,
        borderColor: type === 'secondary' ? borderColor : 'transparent',
      }}
    >
      {leadingIcon ? (
        <Box width={iconSize} height={iconSize} alignItems="center" justifyContent="center">
          <Image
            source={{ uri: ICON_BY_VALIDATION_AND_SIZE[size][validation] }}
            style={{ width: iconSize, height: iconSize }}
          />
        </Box>
      ) : null}

      <Box
        alignItems="center"
        justifyContent="center"
        paddingHorizontal={LABEL_PADDING_HORIZONTAL_BY_SIZE[size]}
        paddingTop="spacing.general.0"
        paddingBottom="spacing.general.x0,5"
      >
        <Text
          variant={TEXT_VARIANT_BY_SIZE[size]}
          style={{
            color: textColor,
            fontFamily: theme.fonts.theFuture.medium,
          }}
        >
          {label}
        </Text>
      </Box>
    </Box>
  );
}
